// Builds the sidebar's Listing Requests sub-nav from the queue registry + live
// counts. Exposed via the module's public surface so the app shell gets a
// ready-made nav shape and the registry stays private (architecture-principles
// §10) — adding a queue never means editing AppSidebar.

import { computed, type ComputedRef } from 'vue'
import { useListingRequestStatusCountsQuery } from '../api/queries/useListingRequestStatusCountsQuery'
import {
  LISTING_REQUEST_NAV_ORDER,
  LISTING_REQUEST_QUEUES,
  type LISTING_REQUEST_NAV_GROUP,
} from '../constants/listingRequestQueues'
import { badgeForQueue } from '../utils/listingRequestQueueUtils'

export interface ListingRequestNavItem {
  label: string
  icon: string
  to: string
  /** Omitted at zero — an empty queue is not an alert. */
  count?: number
}

export interface ListingRequestNavGroup {
  label: string
  items: ListingRequestNavItem[]
}

export function useListingRequestNavGroups(): {
  groups: ComputedRef<ListingRequestNavGroup[]>
} {
  const { data: counts } = useListingRequestStatusCountsQuery()

  const groups = computed<ListingRequestNavGroup[]>(() => {
    // Preserve registry order within each group, and group order by first
    // appearance — so the nav reads Work queues → Awaiting seller → Directory
    // without a second ordering constant to keep in sync.
    const byGroup = new Map<LISTING_REQUEST_NAV_GROUP, ListingRequestNavItem[]>()

    for (const key of LISTING_REQUEST_NAV_ORDER) {
      const queue = LISTING_REQUEST_QUEUES[key]
      const items = byGroup.get(queue.navGroup) ?? []
      items.push({
        label: queue.navLabel,
        icon: queue.icon,
        to: queue.route,
        count: badgeForQueue(counts.value, queue),
      })
      byGroup.set(queue.navGroup, items)
    }

    return [...byGroup.entries()].map(([label, items]) => ({ label, items }))
  })

  return { groups }
}
